import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";
import Step5 from "./Step5";
import Step6 from "./Step6";
import Step7 from "./Step7";
import Step8 from "./Step8";
import Step9 from "./Step9";
import { Box } from "@mui/material";
import { Formik, Form } from "formik";
import { Toaster, toast } from "react-hot-toast";
import { Typography } from "@mui/material";
import { PostJobApi } from "../../../service";
import CompletedRequest from "./CompletedRequest";
import StepperForm from "../common/StepperForm";
import React, { useState, useEffect, useRef } from "react";
import { useUser } from "../../context/UserContext";
import { Title } from "../../constant/Home/PostJob";
import { useLocation, useNavigate } from "react-router-dom";
import { initialValues } from "../../constant/Home/PostJob";
import { stepFieldsMap } from "../../constant/Home/PostJob";
import { validationSchemas, stepsNames } from "../../constant/Home/PostJob";
import { generatePostJobSummary } from "../../constant/Home/Jobs";
import { useStatisticsData } from "../../context/StatisticsData";
import PaymentMethodModal from "../common/PaymentMethodModal";
import AddPropertyForm from "../common/properties/AddProperty";

const buildPostJobPayload = (values) => {
  const formData = new FormData();
  const arrayFields = new Set(["days_available"]);

  Object.entries(values).forEach(([key, value]) => {
    if (key === "media_files" || value === undefined || value === null) {
      return;
    }

    if (arrayFields.has(key) && Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== undefined && entry !== null && entry !== "") {
          formData.append(key, entry);
        }
      });
    } else if (value !== "") {
      const normalized =
        typeof value === "boolean" ? String(value) : value ?? "";
      formData.append(key, normalized);
    }
  });

  if (values.media_files instanceof File) {
    formData.append("media_files", values.media_files);
  }

  return formData;
};

const PostJob = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const propertyFromStateRef = useRef(location.state?.property || null);
  const propertyFromState = propertyFromStateRef.current;
  const { userData } = useUser();
  const { statisticsData, fetchStatistics, refreshStatistics } = useStatisticsData();
  const [jobData, setJobData] = useState({});
  const [submittedValues, setSubmittedValues] = useState(null);
  const [summarySections, setSummarySections] = useState([]);

  const [activeStep, setActiveStep] = useState(0);
  // Guard to prevent duplicate submissions
  const submitLockRef = useRef(false);

  // Local helper state for AddProperty (ID only, modal visibility derived from gating)
  const [addPropertyId, setAddPropertyId] = useState(null);

  // Always reset the lock on mount and when this component unmounts
  useEffect(() => {
    submitLockRef.current = false;
    return () => {
      submitLockRef.current = false;
    };
  }, []);

  // Ensure we have fresh statistics for gating
  useEffect(() => {
    // Fetch on mount to make sure totalProperties is available
    fetchStatistics?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No imperative gating state is needed; we derive gating conditions below

  // If the user restarts the flow (back to step 0), clear the lock
  useEffect(() => {
    if (activeStep === 0) {
      submitLockRef.current = false;
    }
  }, [activeStep]);

  useEffect(() => {
    // Guard: Only sync URL if we are still on the post-job route
    if (location.pathname !== "/post-job") return;

    // Use ref to read propertyFromState — avoids infinite loop in Safari.
    // Calling navigate() updates location.state, which would produce a new
    // object reference for propertyFromState on every render, causing the
    // effect to re-fire and exceed Safari's 100 replaceState/10s limit.
    const prop = propertyFromStateRef.current;

    if (activeStep === 9) {
      navigate("/post-job?step=9", {
        replace: true,
        state: prop ? { property: prop } : undefined,
      });
    } else {
      navigate("/post-job", {
        replace: true,
        state: prop ? { property: prop } : undefined,
      });
    }
  }, [activeStep, navigate, location.pathname]);

  const handleSubmit = async (values, { setSubmitting }) => {
    // Prevent accidental double submits
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    try {
      setSubmittedValues(values);
      setSummarySections(
        generatePostJobSummary(values, {
          propertyLabel: values.property_label,
          unitLabel: values.unit_label,
        })
      );

      const payload = buildPostJobPayload(values);
      const res = await PostJobApi.addPostJob(payload);
      console.log("rez", res);
      setJobData(res?.data);
      setActiveStep(9);
    } catch (error) {
      console.error("Error submitting job:", error);
      const detail =
        error.response?.data?.detail ||
        "Failed to submit job. Please try again.";
      toast.error(detail);
      // Allow retry on error
      submitLockRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  const validateCurrentStep = async () => {
    try {
      const schema = validationSchemas[activeStep];
      if (schema) {
        await schema.validate(values, { abortEarly: false });
      }
      return true;
    } catch (errors) {
      console.log("Validation errors:", errors);
      return false;
    }
  };

  const activeSteps = {
    0: <Step1 />,
    1: <Step2 />,
    2: <Step3 propertyFromState={propertyFromState} />,
    3: <Step4 />,
    4: <Step5 />,
    5: <Step6 />,
    6: <Step7 />,
    7: <Step8 />,
    8: <Step9 />,
    9: (
      <CompletedRequest
        setActiveStep={setActiveStep}
        jobData={jobData}
        submittedValues={submittedValues}
        summarySections={summarySections}
      />
    ),
  };

  return (
    <div className="py-0">
      <Toaster />
      {/* Gating Modals */}
      {(() => {
        const accountType = userData?.accountType;
        const isLH = ["LANDLORD", "PROPERTY_MANAGER", "HOMEOWNER", "STR_HOST", "FAMILY_MEMBER"].includes(accountType);
        const isOwner = ["LANDLORD", "PROPERTY_MANAGER", "HOMEOWNER", "STR_HOST", "FAMILY_MEMBER"].includes(accountType);
        const totalProps = statisticsData?.totalProperties;
        const hasPropsInfo = typeof totalProps === "number";
        const hasPayment = userData?.hasPaymentMethod === true;
        const needsProperty = isLH && hasPropsInfo && totalProps === 0;
        const needsPayment = isOwner && hasPropsInfo && totalProps > 0 && !hasPayment;

        return (
          <>
            {/* Always prompt to add a property if none exist */}
            {needsProperty && accountType !== "FAMILY_MEMBER" && (
              <AddPropertyForm
                setModalState={() => { /* gated: keep open until success */ }}
                onSuccess={() => {
                  // Refresh statistics; once property exists, payment modal may open next
                  refreshStatistics?.();
                  setAddPropertyId(null);
                }}
                setPropertyId={setAddPropertyId}
                propertyId={addPropertyId}
              />
            )}
            {/* Once at least one property exists, require a payment method */}
            <PaymentMethodModal
              open={needsPayment}
              onClose={() => { /* gated: keep open until method added */ }}
              onAdd={() => { /* handled internally */ }}
            />
          </>
        );
      })()}
      <div className="pt-[84px]">
        {/* Do not render the flow until gated requirements are met for LANDLORD/HOMEOWNER */}
        {(() => {
          const accountType = userData?.accountType;
          const isLH = ["LANDLORD", "PROPERTY_MANAGER", "HOMEOWNER", "STR_HOST", "FAMILY_MEMBER"].includes(accountType);
          const isOwner = ["LANDLORD", "PROPERTY_MANAGER", "HOMEOWNER", "STR_HOST", "FAMILY_MEMBER"].includes(accountType);
          const totalProps = statisticsData?.totalProperties;
          const hasPropsInfo = typeof totalProps === "number";
          const hasPayment = userData?.hasPaymentMethod === true;
          const gatingRequired = ((isLH && accountType !== "FAMILY_MEMBER") && hasPropsInfo && totalProps === 0) || (isOwner && hasPropsInfo && totalProps > 0 && !hasPayment);
          const waitingForInfo = (isLH || accountType === "FAMILY_MEMBER") && !hasPropsInfo; // wait for stats
          if (gatingRequired || waitingForInfo) return null;
          return (
            <Formik
              initialValues={{
                ...initialValues,
                account_type: userData?.accountType,
                property: propertyFromState?.id || initialValues.property,
              }}
              validationSchema={validationSchemas[activeStep]}
              onSubmit={handleSubmit}
              enableReinitialize={true}
              validateOnChange={true}
              validateOnBlur={true}
              context={{ userAccountType: userData?.accountType }}
            >
              {({
                values,
                errors,
                touched,
                setFieldValue,
                validateForm,
                handleSubmit,
              }) => (
                <Form>
                  <StepperForm
                    stepsNames={stepsNames}
                    activeStep={activeStep}
                    setActiveStep={setActiveStep}
                    children={activeSteps[activeStep]}
                    footerText={false}
                    onStepValidation={validateCurrentStep}
                    setFieldValue={setFieldValue}
                    errors={errors}
                    touched={touched}
                    stepFieldsMap={stepFieldsMap}
                    handleSubmit={handleSubmit}
                    showDashboardButton={false}
                    submitStep={8}
                    showCompletionScreen={activeStep === 9}
                    submittedValues={submittedValues}
                  >
                    <Box
                      sx={{
                        maxWidth:
                          activeStep === 0
                            ? "898px"
                            : activeStep === 3
                            ? "1088px"
                            : "720px",
                        borderRadius: "16px",
                        paddingY: { md: 2, xs: 1 },
                        paddingX: { md: 4, xs: 1 },
                        gap: { md: 2, xs: 2 },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        border: "1px solid #ECECEC",
                        width: "100%",
                      }}
                    >
                      {activeStep !== 9 && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="h1"
                            sx={{
                              color: "#242424",
                              lineHeight: "32px",
                              textAlign: "center",
                              fontSize: { md: "22px", xs: "20px" },
                              "@media (max-width: 768px)": {
                                lineHeight: "normal",
                              },
                            }}
                          >
                            {Title[activeStep].title}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#595959",
                              fontSize: { md: "18px", xs: "16px" },
                              textAlign: "center",
                              lineHeight: "26px",
                              "@media (max-width: 768px)": {
                                lineHeight: "20px",
                              },
                            }}
                          >
                            {Title[activeStep].subTitle}
                          </Typography>
                        </Box>
                      )}
                      {activeSteps[activeStep]}
                    </Box>
                  </StepperForm>
                </Form>
              )}
            </Formik>
          );
        })()}
      </div>
    </div>
  );
};

export default PostJob;
